import { useState } from 'react';
import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  BarChart3,
  Bell,
  Building2,
  CalendarClock,
  Check,
  CreditCard,
  FolderKanban,
  HelpCircle,
  Home,
  ListChecks,
  LogOut,
  Menu,
  PlayCircle,
  Plug,
  Search,
  SlidersHorizontal,
  User as UserIcon,
  Users,
  X,
} from 'lucide-react';

import { useAuth } from '../lib/auth/AuthProvider';
import { useHideBilling } from '../lib/billing';
import { useHasFeature, FEATURE_CALENDAR_AGENDA } from '../lib/features';
import { useCompanySwitch } from '../lib/useCompanySwitch';
import { roleLabel } from '../lib/roles';
import { isCompanyAdmin } from '../lib/team';
import { t } from '../lib/i18n';
import { cn } from '../lib/cn';
import { LanguageSwitch } from './LanguageSwitch';
import { ThemeToggle } from './ThemeToggle';
import { Avatar } from './board/Avatar';
import { Spinner } from './ui';
import { useTour } from './tour/ProductTour';

type IconType = ComponentType<{ size?: number | string; className?: string }>;

/** Item de navegación del drawer con estado activo; cierra el drawer al tocar. */
function DrawerLink({
  to,
  icon: Icon,
  label,
  end,
  onNavigate,
}: {
  to: string;
  icon: IconType;
  label: string;
  end?: boolean;
  onNavigate: () => void;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-token px-3 py-2.5 text-sm font-medium transition-colors',
          isActive ? 'bg-brand/10 text-brand' : 'text-ink hover:bg-surface-2',
        )
      }
    >
      <Icon size={18} className="shrink-0" />
      {label}
    </NavLink>
  );
}

/** Encabezado de sección dentro del drawer. */
function SectionLabel({ children }: { children: string }) {
  return (
    <p className="px-3 pb-1 pt-4 font-mono text-[11px] uppercase tracking-[0.14em] text-muted">
      {children}
    </p>
  );
}

/**
 * Menú sándwich (hamburguesa) para móvil. Botón visible sólo en `< sm`; abre un
 * drawer lateral (Radix Dialog: overlay + foco atrapado + cierre por Esc/tap
 * fuera) con la navegación completa de la app de empresa y las acciones de
 * cuenta. En desktop no se renderiza nada (la topbar ya tiene nav + dropdown).
 */
export function AppMobileNav({
  onSwitched,
  onOpenSearch,
}: {
  onSwitched?: (name: string) => void;
  /** Abre el buscador global (el estado vive en la topbar). */
  onOpenSearch?: () => void;
}) {
  const { user, company, companies, logout } = useAuth();
  const { startTour } = useTour();
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const canManageTeam = isCompanyAdmin(user?.role);
  const hasAgenda = useHasFeature(FEATURE_CALENDAR_AGENDA);
  // Empresa ACTIVE que no paga nada (monto 0) → ocultar "Facturación".
  const hideBilling = useHideBilling(canManageTeam);
  const { switchingId, run: runSwitch } = useCompanySwitch(onSwitched);

  const close = () => setOpen(false);

  const handleSwitch = async (id: string, name: string, role: string) => {
    if (id === company?.id) {
      close();
      return;
    }
    close();
    await runSwitch({ id, name, role });
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          aria-label={t('menu.open')}
          className="grid h-9 w-9 place-items-center rounded-token border border-line bg-surface-2 text-ink transition-colors hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand sm:hidden"
        >
          <Menu size={18} />
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="overlay-in fixed inset-0 z-40 bg-ink/50 backdrop-blur-[2px] sm:hidden" />
        <Dialog.Content
          aria-describedby={undefined}
          className="drawer-in-left fixed inset-y-0 left-0 z-50 flex w-[86%] max-w-xs flex-col border-r border-line bg-surface shadow-lift focus:outline-none sm:hidden"
        >
          {/* Cabecera: marca + cerrar */}
          <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
            <div className="flex items-center gap-2 overflow-hidden">
              <img
                src={`${import.meta.env.BASE_URL}brand/logo-mark.png`}
                alt="Tembiapo"
                className="h-7 w-7 shrink-0"
              />
              <Dialog.Title className="truncate font-display font-semibold text-ink">
                {company?.name ?? 'Tembiapo'}
              </Dialog.Title>
            </div>
            <Dialog.Close
              aria-label={t('menu.close')}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-token text-muted transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <X size={18} />
            </Dialog.Close>
          </div>

          {/* Identidad del usuario */}
          {user && (
            <div className="flex items-center gap-3 border-b border-line px-4 py-3">
              <Avatar member={user} size="md" className="h-9 w-9" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">
                  {user.first_name} {user.last_name}
                </p>
                <p className="truncate text-xs text-muted">{user.email}</p>
              </div>
            </div>
          )}

          {/* Navegación + cuenta (scrolleable) */}
          <nav className="flex-1 overflow-y-auto px-2 pb-4">
            {companies.length > 1 && (
              <>
                <SectionLabel>{t('company.switch.title')}</SectionLabel>
                {companies.map((c) => {
                  const isCurrent = c.id === company?.id;
                  const isBusy = switchingId === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      disabled={switchingId !== null}
                      onClick={() => void handleSwitch(c.id, c.name, c.role)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-token px-3 py-2.5 text-left text-sm font-medium transition-colors disabled:opacity-60',
                        isCurrent ? 'bg-brand/10 text-brand' : 'text-ink hover:bg-surface-2',
                      )}
                    >
                      <Building2 size={18} className="shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{c.name}</span>
                        <span className="block truncate text-xs text-muted">{roleLabel(c.role)}</span>
                      </span>
                      {isBusy ? (
                        <Spinner className="h-4 w-4 shrink-0" />
                      ) : isCurrent ? (
                        <Check size={16} className="shrink-0 text-brand" />
                      ) : null}
                    </button>
                  );
                })}
              </>
            )}

            <SectionLabel>{t('menu.sectionNav')}</SectionLabel>
            {onOpenSearch && (
              <button
                type="button"
                onClick={() => {
                  close();
                  onOpenSearch();
                }}
                className="flex w-full items-center gap-3 rounded-token px-3 py-2.5 text-left text-sm font-medium text-ink transition-colors hover:bg-surface-2"
              >
                <Search size={18} className="shrink-0" />
                {t('search.open')}
              </button>
            )}
            <DrawerLink to="/inicio" end icon={Home} label={t('nav.home')} onNavigate={close} />
            <DrawerLink to="/proyectos" icon={FolderKanban} label={t('nav.projects')} onNavigate={close} />
            <DrawerLink to="/mis-tareas" icon={ListChecks} label={t('nav.myTasks')} onNavigate={close} />
            {hasAgenda && (
              <DrawerLink to="/agenda" icon={CalendarClock} label={t('nav.agenda')} onNavigate={close} />
            )}
            <DrawerLink to="/reportes" icon={BarChart3} label={t('nav.reports')} onNavigate={close} />
            {canManageTeam && (
              <DrawerLink to="/equipo" icon={Users} label={t('nav.team')} onNavigate={close} />
            )}

            <SectionLabel>{t('menu.sectionAccount')}</SectionLabel>
            <DrawerLink to="/perfil" icon={UserIcon} label={t('nav.profile')} onNavigate={close} />
            <DrawerLink to="/notificaciones" icon={Bell} label={t('notifications.title')} onNavigate={close} />
            <DrawerLink to="/preferencias" icon={SlidersHorizontal} label={t('nav.preferences')} onNavigate={close} />
            <DrawerLink to="/conectores" icon={Plug} label={t('nav.connectors')} onNavigate={close} />
            {canManageTeam && !hideBilling && (
              <DrawerLink to="/facturacion" icon={CreditCard} label={t('nav.billing')} onNavigate={close} />
            )}
            <DrawerLink to="/ayuda" icon={HelpCircle} label={t('help.nav.help')} onNavigate={close} />
            <button
              type="button"
              onClick={() => {
                close();
                startTour();
              }}
              className="flex w-full items-center gap-3 rounded-token px-3 py-2.5 text-left text-sm font-medium text-ink transition-colors hover:bg-surface-2"
            >
              <PlayCircle size={18} className="shrink-0" />
              {t('tour.replay')}
            </button>
          </nav>

          {/* Pie: idioma, tema y cerrar sesión */}
          <div className="border-t border-line px-4 py-3">
            <div className="flex items-center justify-between gap-2 py-1">
              <span className="text-xs text-muted">{t('menu.language')}</span>
              <LanguageSwitch />
            </div>
            <div className="flex items-center justify-between gap-2 py-1">
              <span className="text-xs text-muted">{t('menu.theme')}</span>
              <ThemeToggle />
            </div>
            <button
              type="button"
              onClick={() => void handleLogout()}
              disabled={loggingOut}
              className="mt-2 flex w-full items-center gap-3 rounded-token border border-line px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-danger hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-50"
            >
              <LogOut size={18} className="shrink-0" />
              {t('home.userMenu.logout')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
